import { Link } from "react-router-dom";
import type { GossipArticle } from "@/mocks/gossipData";

interface ArticleCardProps {
  article: GossipArticle;
  className?: string;
}

function formatCount(value: number) {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return String(value);
}

export default function ArticleCard({ article, className = "" }: ArticleCardProps) {
  return (
    <article
      className={`group flex h-full flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300 ${className}`}
    >
      <Link
        to={`/gossip/${article.id}`}
        className="relative block h-44 w-full overflow-hidden md:h-48 cursor-pointer"
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt={article.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-3 py-1 text-xs font-label text-primary-700">
          {article.category}
        </span>
        {article.isHot && (
          <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-label text-background-50">
            <i className="ri-fire-line" aria-hidden="true" />
            热瓜
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-lg leading-snug text-foreground-950">
          <Link to={`/gossip/${article.id}`} className="transition-colors hover:text-primary-600 cursor-pointer">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {article.summary}
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          {article.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-label text-secondary-900"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line" aria-hidden="true" />
            {article.date}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line" aria-hidden="true" />
            {formatCount(article.readCount)}
          </span>
        </div>
      </div>
    </article>
  );
}

export { formatCount };